import { ReactNode, useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { Store } from "redux";
import { RootSchema } from "../../redux/reducers";
import { analyticsClient } from "./client";
import { getMappedAnalyticsEvents } from "./eventMap";
import { buildAnalyticsEvent, resolveTenantType, resolveTrialUser } from "./payload";
import { registerAnalyticsStateGetter } from "./stateAccess";

interface AnalyticsTrackProps {
  children: ReactNode;
}

export const getAnalyticsInitConfig = (authConfig?: RootSchema["auth"]["authConfig"]) => {
  if (!authConfig?.isSuccess) {
    return undefined;
  }

  return {
    amplitudeAppKey: authConfig.amplitudeAppKey,
    postHogToken: authConfig.postHogToken,
    postHogHost: authConfig.postHogHost
  };
};

const getUserEmail = (state: RootSchema) => state.auth?.intercomUserData?.response?.email;

const AnalyticsTrack: React.FC<AnalyticsTrackProps> = ({ children }) => {
  const store = useStore() as Store<RootSchema>;
  const previousStateRef = useRef<RootSchema>(store.getState());
  const initializedAuthConfigKeyRef = useRef<string>("");
  const identifiedUserRef = useRef<string | undefined>();
  const currentAppIdRef = useRef<string | number | undefined>();
  const processedUserPropertiesIdRef = useRef<number>(0);
  const processedResetIdRef = useRef<number>(0);

  useEffect(() => {
    registerAnalyticsStateGetter(() => store.getState());

    const syncAnalytics = () => {
      const previousState = previousStateRef.current;
      const currentState = store.getState();
      const authConfig = currentState.auth?.authConfig;
      const analyticsConfig = getAnalyticsInitConfig(authConfig);
      const analyticsConfigKey = analyticsConfig ? `${analyticsConfig.amplitudeAppKey || ""}:${analyticsConfig.postHogToken || ""}:${analyticsConfig.postHogHost || ""}` : "";

      if (analyticsConfig && initializedAuthConfigKeyRef.current !== analyticsConfigKey) {
        initializedAuthConfigKeyRef.current = analyticsConfigKey;
        analyticsClient.init(analyticsConfig);
      }

      const email = getUserEmail(currentState);
      if (email && identifiedUserRef.current !== email) {
        identifiedUserRef.current = email;
        analyticsClient.identify({
          userId: email,
          traits: {
            email,
            recurly_id: currentState.auth?.intercomUserData?.response?.recurlyId
          }
        });
      }

      const currentAppInfo = currentState.auth?.currentAppinfo;
      if (currentAppInfo?.AppId && currentAppIdRef.current !== currentAppInfo.AppId) {
        currentAppIdRef.current = currentAppInfo.AppId;
        const trialUser = resolveTrialUser(currentAppInfo);
        const tenantType = resolveTenantType(currentState, currentAppInfo);
        analyticsClient.setUserProperties({
          ...(typeof trialUser === "boolean" ? { trial_user: trialUser } : {}),
          ...(tenantType ? { tenantType } : {}),
          app_id: currentAppInfo.AppId,
          app_name: currentAppInfo.AppName
        });
      }

      const userProperties = currentState.analytics?.userProperties;
      if (userProperties?.id && processedUserPropertiesIdRef.current !== userProperties.id) {
        processedUserPropertiesIdRef.current = userProperties.id;
        analyticsClient.setUserProperties(userProperties.properties);
      }

      if (currentState.analytics?.resetId && processedResetIdRef.current !== currentState.analytics.resetId) {
        processedResetIdRef.current = currentState.analytics.resetId;
        analyticsClient.reset();
      }

      getMappedAnalyticsEvents(previousState, currentState).forEach((event) => analyticsClient.track(buildAnalyticsEvent(event, currentState)));
      previousStateRef.current = currentState;
    };

    syncAnalytics();
    return store.subscribe(syncAnalytics);
  }, [store]);

  return <>{children}</>;
};

export default AnalyticsTrack;
